import styled from "styled-components";
import Button from "../common/Button";

const FILE_LIST_ITEM_HEIGHT = 48;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

export const DropZone = styled.div`
  width: 100%;
  height: ${FILE_LIST_ITEM_HEIGHT}px;
  border: 2px dashed #E5E2DD;
  border-radius: 8px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s ease;
  cursor: pointer;
  position: relative;
  margin-top: 8px;

  &:hover {
    border-color: #D4C5B1;
    background: #F8F6F3;
  }
`;

export const FileInput = styled.input`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  opacity: 0;
  cursor: pointer;
  z-index: -1;
`;

export const UploadIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #686766;
  flex-shrink: 0;
`;

export const DropZoneText = styled.div`
  color: #686766;
  display: flex;
  gap: 4px;
  font-size: 14px;
`;

export const AttachedFilesSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

export const FileList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FileListHeader = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #1A1A1A;
  margin-bottom: 6px;
`;

export const FileItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background-color: #F8F6F3;
  border: 1px solid #E5E2DD;
  border-radius: 6px;
  font-size: 12px;
  color: #4B5563;
  transition: background 0.2s, border 0.2s;

  &:hover {
    background: #F0E4D3;
    border-color: #D4C5B1;
  }
`;

export const FileName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  margin-right: 6px;
`;

export const ArtifactsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

export const ArtifactList = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ArtifactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: #F8F6F3;
  border: 1px solid #E5E2DD;
  border-radius: 6px;
  font-size: 12px;
  color: #4B5563;
  transition: background 0.2s, border 0.2s;
  position: relative;
  cursor: pointer;

  &:hover {
    background: #F0E4D3;
    border-color: #D4C5B1;
  }
`;

export const ArtifactIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #686766;
  flex-shrink: 0;
`;

export const ArtifactTextColumn = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`;

export const ArtifactName = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: 500;
  color: #1A1A1A;
`;

export const ArtifactDescription = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 11px;
  color: #6B7280;
`;

export const ArtifactDot = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$flashing',
})<{ $flashing: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #DC2626;
  flex-shrink: 0;
  box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  ${props => props.$flashing && `
    animation: artifact-dot-glow 1s ease-out infinite;
  `}

  @keyframes artifact-dot-glow {
    0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.75); }
    70% { box-shadow: 0 0 0 6px rgba(220, 38, 38, 0); }
    100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
  }
`;

export const ArtifactActions = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  margin-left: auto;
`;

export const ArtifactActionButton = styled.button`
  background: none;
  border: none;
  padding: 3px;
  cursor: pointer;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 150ms ease;

  &:hover {
    background-color: #E5E2DD;
    color: #4B5563;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

export const FolderRow = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 4px;
  font-size: 11px;
  font-weight: 500;
  color: #6B7280;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 150ms ease;

  &:hover {
    background-color: var(--background-primary-canvas);
  }
`;

export const FolderChevron = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #9CA3AF;
`;

export const FolderChildren = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 14px;
  border-left: 1px solid #E5E2DD;
  margin-left: 6px;
`;

export const ManagementSection = styled.div`
  width: 100%;
  border-top: 1px solid #E5E2DD;
  padding-top: 12px;
  display: flex;
  flex-direction: column;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
`;

export const SectionTitle = styled.h3`
  font-size: 13px;
  font-weight: 500;
  color: #1A1A1A;
  margin: 0;
`;

export const AddButton = styled(Button)`
  background: transparent;
  border: 1px solid #E5E2DD;
  color: #686766;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  transition: all 250ms ease;

  &:hover {
    background-color: #EDEBE8;
    border-color: #D4C5B1;
  }
`;

export const FilesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
`;


export const FileListItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isManaged',
})<{ isManaged?: boolean }>`
  font-size: 12px;
  border-radius: 4px;
  padding: 4px 8px;
  cursor: pointer;
  transition: 250ms ease;
  background-color: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &:hover {
    background-color: #EDEBE8;
  }
`;

export const FileNameWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-grow: 1;
  overflow: hidden;
`;

export const FileNameText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const FileActions = styled.div`
  display: flex;
  gap: 4px;
  transition: opacity 150ms ease;
`;

export const HoverActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 150ms ease;
  opacity: 0;

  ${FileListItem}:hover & {
    opacity: 1;
  }

  &:hover {
    background-color: #E5E2DD;
    color: #4B5563;
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

export const ActionButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #6B7280;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 150ms ease;

  &:hover {
    background-color: #E5E2DD;
    color: #4B5563;
  }
`;

export const FileTag = styled.span`
  background-color: var(--background-primary-canvas);
  color: var(--primary);
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  font-family: inherit;
  font-weight: 400;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  border: 1px solid var(--primary);
`;

export const LoaderWrapper = styled.div`
  justify-content: center;
  align-items: center;
`;
